import { fetchWithAuth } from '../utils/fetchWithAuth';

const getApiUrl = () => {
    const url = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const promptService = {
    /**
     * Gets the current system prompt for an engine version
     * @param {string} engineVersion - Engine version (e.g., 'v1', 'v2', or 'default')
     */
    getPrompt: async (engineVersion = 'default') => {
        const queryParam = engineVersion && engineVersion !== 'default' ? `?engineVersion=${engineVersion}` : '';
        const response = await fetchWithAuth(`${getApiUrl()}/prompt/get${queryParam}`, {
            method: 'GET'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to fetch prompt: ${response.statusText}`);
        }

        return await response.json();
    },

    /**
     * Updates the system prompt for an engine version
     * @param {object} updateRequest - Prompt update request
     * @param {string} engineVersion - Engine version (e.g., 'v1', 'v2', or 'default')
     */
    async updatePrompt(updateRequest, engineVersion = 'default') {
        const url = getApiUrl();
        const queryParam = engineVersion && engineVersion !== 'default' ? `?engineVersion=${engineVersion}` : '';
        const response = await fetchWithAuth(`${url}/prompt/update${queryParam}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateRequest)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    },

    /**
     * Gets prompt history for an engine version
     * @param {string} engineVersion - Engine version (e.g., 'v1', 'v2', or 'default')
     */
    async getPromptHistory(engineVersion = 'default') {
        const url = getApiUrl();
        const queryParam = engineVersion && engineVersion !== 'default' ? `?engineVersion=${engineVersion}` : '';
        const response = await fetchWithAuth(`${url}/prompt/history${queryParam}`, {
            method: 'GET'
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    },

    /**
     * Gets a specific historical prompt version
     * @param {number} version - Version number
     * @param {string} engineVersion - Engine version (e.g., 'v1', 'v2', or 'default')
     */
    async getPromptHistoryVersion(version, engineVersion = 'default') {
        const url = getApiUrl();
        const queryParam = engineVersion && engineVersion !== 'default' ? `?engineVersion=${engineVersion}` : '';
        const response = await fetchWithAuth(`${url}/prompt/history/${version}${queryParam}`, {
            method: 'GET'
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    },

    /**
     * Copies prompt from one engine version to another
     * @param {string} fromEngineVersion - Source engine version
     * @param {string} toEngineVersion - Target engine version
     */
    async copyPromptToEngineVersion(fromEngineVersion, toEngineVersion) {
        // First get the source prompt
        const sourcePrompt = await this.getPrompt(fromEngineVersion);

        // Then save it to the target engine version
        const updateRequest = {
            systemPromptTemplate: sourcePrompt.systemPromptTemplate || sourcePrompt.SystemPromptTemplate,
            description: sourcePrompt.description || sourcePrompt.Description,
            changeNotes: `Copied from engine ${fromEngineVersion}`
        };

        return await this.updatePrompt(updateRequest, toEngineVersion);
    },

    /**
     * Gets the built-in Meeting AI instruction defaults (summary / Q&A / suggested-questions)
     * so the editor can pre-fill the textarea and offer "Reset to default".
     * @returns {Promise<{summary: string, qa: string, suggested: string}>}
     */
    async getMeetingAiPromptDefaults() {
        const response = await fetchWithAuth(`${getApiUrl()}/meeting-ai/prompts/defaults`, {
            method: 'GET'
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return response.json();
    }
};

export default promptService;
